import { Checkbox, Text, Title } from "@clipboard-health/ui-components";
import { Box, FormControlLabel, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { LoadingButton } from "@src/appV2/lib/LoadingButton/LoadingButton";
import { useMemo, useState } from "react";

import { OrientationListItems } from "../OrientationListItems";

interface RecapStepProps {
  recapText: string[];
  confirmationText: string;
  isOrientationBeingSubmitted: boolean;
  isOrientationDataLoading: boolean;
  onContinue: () => void;
  stepInfo: string;
  // Useful for tracking events in the recap step
  workerId?: string;
  assessmentId?: string;
  stepNumber?: number;
}

export function RecapStep(props: RecapStepProps) {
  const {
    recapText,
    confirmationText,
    stepInfo,
    isOrientationBeingSubmitted,
    isOrientationDataLoading,
    onContinue,
    workerId,
    assessmentId,
    stepNumber,
  } = props;
  const [isConfirmed, setIsConfirmed] = useState(false);
  const { showInfoToast } = useToast();

  const commonLogProperties = useMemo(() => {
    return {
      ...(workerId && { workerId }),
      ...(assessmentId && { assessmentId }),
      ...(stepNumber && { stepNumber }),
    };
  }, [workerId, assessmentId, stepNumber]);

  useLogEffect(APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_RECAP_STEP_VIEWED, commonLogProperties);

  return (
    <Stack flex={1} pt={6} justifyContent="space-between">
      <Stack>
        <Title bold variant="h3" component="h3">
          Quick Recap
        </Title>

        <OrientationListItems
          items={recapText.map((text, index) => ({
            text,
            variant: "body1",
            ...(index === recapText.length - 1 && {
              bold: true,
            }),
          }))}
          sx={{
            py: 6,
          }}
        />
        <FormControlLabel
          sx={{
            alignItems: "start",
            p: 1, // Increase target area for checkbox
            // Override default MUI negative margin (-11px) to prevent component overflowing its container
            // See: https://github.com/mui/material-ui/issues/19472
            m: 4,
          }}
          control={
            <Checkbox
              checked={isConfirmed}
              onChange={(event) => {
                setIsConfirmed(event.target.checked);
              }}
            />
          }
          label={
            <Text variant="body1" color="text.primary" ml={6}>
              {confirmationText}
            </Text>
          }
        />
      </Stack>
      <Stack justifyContent="space-between" alignItems="center" spacing={4}>
        <Text bold variant="h6" color="text.secondary">
          {stepInfo}
        </Text>
        <Box
          sx={{
            width: "100%",
          }}
          // Disabled button can't be clicked but we want to give feedback to the user if they try to click this area
          onClick={() => {
            if (!isConfirmed) {
              showInfoToast("Please select the checkbox before continuing");
            }
          }}
        >
          <LoadingButton
            fullWidth
            variant="contained"
            LinkComponent="button"
            disabled={!isConfirmed || isOrientationDataLoading}
            isLoading={isOrientationBeingSubmitted}
            sx={{ alignSelf: "center" }}
            onClick={() => {
              onContinue();
            }}
          >
            Continue
          </LoadingButton>
        </Box>
      </Stack>
    </Stack>
  );
}
